// Auth endpoint bindings. Mirrors iOS AuthEndpoints + the /auth/* routes.
import { api } from "./client";
import { tokenStore } from "./token-store";
import type {
  AccessTokenResponse,
  LoginPayload,
  RegisterPayload,
  TokenResponse,
  User,
} from "./types";

export async function register(payload: RegisterPayload): Promise<TokenResponse> {
  const tokens = await api.post<TokenResponse>("/auth/register", payload, { auth: false });
  tokenStore.setTokens(tokens.access_token, tokens.refresh_token);
  return tokens;
}

export async function login(payload: LoginPayload): Promise<TokenResponse> {
  const tokens = await api.post<TokenResponse>("/auth/login", payload, { auth: false });
  tokenStore.setTokens(tokens.access_token, tokens.refresh_token);
  return tokens;
}

export async function refresh(): Promise<AccessTokenResponse | null> {
  const refresh_token = tokenStore.refresh;
  if (!refresh_token) return null;
  const res = await api.post<AccessTokenResponse>("/auth/refresh", { refresh_token }, {
    auth: false,
  });
  tokenStore.setAccess(res.access_token);
  return res;
}

export async function logout(): Promise<void> {
  const refresh_token = tokenStore.refresh;
  try {
    if (refresh_token) await api.post<void>("/auth/logout", { refresh_token }, { auth: false });
  } finally {
    tokenStore.clear();
  }
}

export function getMe(): Promise<User> {
  return api.get<User>("/auth/me");
}

// ── Email verification ─────────────────────────────────────────────────────────

export function verifyEmail(token: string): Promise<{ status: string }> {
  return api.post<{ status: string }>("/auth/verify-email", { token }, { auth: false });
}

export function resendVerification(): Promise<{ status: string }> {
  return api.post<{ status: string }>("/auth/resend-verification", undefined);
}

// ── Password management ─────────────────────────────────────────────────────────

export function forgotPassword(email: string): Promise<{ status: string }> {
  return api.post<{ status: string }>("/auth/forgot-password", { email }, { auth: false });
}

export function resetPassword(token: string, new_password: string): Promise<{ status: string }> {
  return api.post<{ status: string }>(
    "/auth/reset-password",
    { token, new_password },
    { auth: false },
  );
}

export async function changePassword(
  current_password: string,
  new_password: string,
): Promise<TokenResponse> {
  const tokens = await api.patch<TokenResponse>("/auth/change-password", {
    current_password,
    new_password,
  });
  // Server rotates refresh tokens; adopt the fresh pair so the session survives.
  tokenStore.setTokens(tokens.access_token, tokens.refresh_token);
  return tokens;
}

// ── OAuth (Apple / Google) ──────────────────────────────────────────────────────

export async function oauthApple(
  identity_token: string,
  full_name?: string,
): Promise<TokenResponse> {
  const tokens = await api.post<TokenResponse>(
    "/auth/oauth/apple",
    { identity_token, full_name },
    { auth: false },
  );
  tokenStore.setTokens(tokens.access_token, tokens.refresh_token);
  return tokens;
}

export async function oauthGoogle(id_token: string): Promise<TokenResponse> {
  const tokens = await api.post<TokenResponse>(
    "/auth/oauth/google",
    { id_token },
    { auth: false },
  );
  tokenStore.setTokens(tokens.access_token, tokens.refresh_token);
  return tokens;
}
